// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The navigation bar (M3-37, 08 §3).
//
// Which tabs, which one is active and whether a destination exists are all decided in
// `navigation.ts` and `routes.ts`, where they have tests. What is here is the drawing, and the
// two things about the drawing that are requirements rather than taste:
//
//   * **44 × 44 px targets with a 12 px label**, from the HCC conventions. Enforced in the
//     stylesheet by `min-width`/`min-height` on `.nav__tab` rather than by a fixed size, so a
//     tab whose label wraps grows instead of clipping.
//   * **Three cues for the active tab** — a filled glyph instead of an outline, the accent
//     colour, and an indicator bar — so it is never colour-dependent alone. `aria-current` is the
//     fourth, for anybody who is not looking at it at all.
//
// An unbuilt destination renders as a **disabled** control naming what it is waiting for, rather
// than as a link to a page that apologises. See `routes.ts` for why.

import { Link } from 'react-router'
import { NAV_TABS, isTabActive, navPlacement, tabsFor, type NavTab } from '../navigation'
import { unavailableReason } from '../routes'
import { Icon } from './Icon'
import { useLocalisation } from '../i18n/useLanguage'

export interface BottomNavProps {
  /** The viewport width in CSS pixels, which decides how many tabs there are and where. */
  readonly width: number
  readonly pathname: string
}

export function BottomNav({ width, pathname }: BottomNavProps): React.JSX.Element {
  const { t } = useLocalisation()
  const placement = navPlacement(width)
  // The header shows every destination: a desktop has the room, and the reason for dropping two
  // of them was never anything but width.
  const tabs = placement === 'header' ? NAV_TABS : tabsFor(width)

  return (
    <nav
      className={placement === 'header' ? 'nav nav--header' : 'nav'}
      aria-label={t('nav.primary')}
    >
      {tabs.map((tab) => (
        <Tab key={tab.id} tab={tab} active={isTabActive(tab, pathname)} />
      ))}
    </nav>
  )
}

function Tab({ tab, active }: { tab: NavTab; active: boolean }): React.JSX.Element {
  const { t } = useLocalisation()
  const unavailable = unavailableReason(tab.to)
  const className = tab.emphasis ? 'nav__tab nav__tab--emphasis' : 'nav__tab'
  const body = (
    <>
      <span className={tab.emphasis ? 'nav__plate' : undefined}>
        <Icon name={tab.id} filled={active} />
      </span>
      <span className="nav__label">{t(tab.label)}</span>
    </>
  )

  if (unavailable !== undefined) {
    // A `button` rather than a dimmed link, because a disabled link is not a thing HTML has: an
    // anchor with no `href` is unfocusable, and one with an `href` is still followable by
    // keyboard however grey it looks.
    return (
      <button
        type="button"
        className={className}
        title={unavailable}
        aria-disabled="true"
        disabled
      >
        {body}
      </button>
    )
  }

  return (
    // **`Link`, not `NavLink`.** `NavLink` computes `aria-current` from its own match against `to`
    // and treats the prop as the value to use *when it agrees* — an `undefined` becomes its default
    // `"page"`. So it lit the colour and the indicator bar (both keyed on `[aria-current]`) by its
    // own prefix rule while the glyph followed `isTabActive`, and the two agreed only while
    // `isTabActive` was the same prefix rule. One answer now drives all four cues.
    <Link
      to={tab.to}
      className={className}
      title={t(tab.title)}
      aria-current={active ? 'page' : undefined}
    >
      {body}
    </Link>
  )
}
